/*
 * Copyright Elasticsearch B.V. and/or licensed to Elasticsearch B.V. under one
 * or more contributor license agreements. Licensed under the Elastic License
 * 2.0 and the Server Side Public License, v 1; you may not use this file except
 * in compliance with, at your election, the Elastic License 2.0 or the Server
 * Side Public License, v 1.
 */

import React, { Component } from 'react';
import PropTypes from "prop-types";
import classNames from 'classnames';
import { keysOf } from '../../common';
import { EuiValidatableControl } from '../validatable_control';
import { EuiButtonEmpty } from '../../button';
import { EuiProgress } from '../../progress';
import { EuiIcon } from '../../icon';
import { EuiI18n } from '../../i18n';
import { EuiLoadingSpinner } from '../../loading';
import { htmlIdGenerator } from '../../../services/accessibility';
import { FormContext } from '../eui_form_context';
var displayToClassNameMap = {
  default: null,
  large: 'euiFilePicker--large'
};
export var DISPLAYS = keysOf(displayToClassNameMap);
export class EuiFilePicker extends Component {
  static contextType = FormContext;
  static defaultProps = {
    initialPromptText: <EuiI18n token="euiFilePicker.promptText" default="Select or drag and drop a file" />,
    compressed: false,
    display: 'large'
  };
  state = {
    promptText: null,
    isHoveringDrop: false
  };
  fileInput = null;
  generatedId = htmlIdGenerator()();
  handleChange = () => {
    if (!this.fileInput) return;
    if (this.fileInput.files && this.fileInput.files.length > 1) {
      this.setState({
        promptText: <EuiI18n token="euiFilePicker.filesSelected" default="{fileCount} files selected" values={{
          fileCount: this.fileInput.files.length
        }} />
      });
    } else if (this.fileInput.files && this.fileInput.files.length === 0) {
      this.setState({
        promptText: null
      });
    } else {
      this.setState({
        promptText: this.fileInput.value.split('\\').pop()
      });
    }
    const {
      onChange
    } = this.props;
    if (onChange) {
      onChange(this.fileInput.files);
    }
  };
  removeFiles = e => {
    if (e) {
      e.stopPropagation();
      e.preventDefault();
    }
    if (!this.fileInput) return;
    this.fileInput.value = '';
    this.handleChange();
  };
  showDrop = () => {
    if (!this.props.disabled) {
      this.setState({
        isHoveringDrop: true
      });
    }
  };
  hideDrop = () => {
    this.setState({
      isHoveringDrop: false
    });
  };
  render() {
    const {
      defaultFullWidth
    } = this.context;
    return <EuiI18n token="euiFilePicker.clearSelectedFiles" default="Clear selected files">
        {clearSelectedFiles => {
        const {
          id,
          name,
          initialPromptText,
          className,
          disabled,
          compressed,
          onChange,
          isInvalid,
          fullWidth = defaultFullWidth,
          isLoading,
          display,
          ...rest
        } = this.props;
        const promptId = `${id || this.generatedId}-filePicker__prompt`;
        const isOverridingInitialPrompt = this.state.promptText != null;
        const normalFormControl = display === 'default';
        const classes = classNames('euiFilePicker', displayToClassNameMap[display], {
          euiFilePicker__showDrop: this.state.isHoveringDrop,
          'euiFilePicker--compressed': compressed,
          'euiFilePicker--fullWidth': fullWidth,
          'euiFilePicker-isInvalid': isInvalid,
          'euiFilePicker-isLoading': isLoading,
          'euiFilePicker-hasFiles': isOverridingInitialPrompt
        }, className);
        let clearButton;
        if (isLoading && normalFormControl) {
          // Override clear button with loading spinner if it is in loading state
          clearButton = <EuiLoadingSpinner className="euiFilePicker__loadingSpinner" />;
        } else if (isOverridingInitialPrompt && !disabled) {
          if (normalFormControl) {
            clearButton = <button type="button" aria-label={clearSelectedFiles} className="euiFilePicker__clearButton" onClick={this.removeFiles}>
                  <EuiIcon className="euiFilePicker__clearIcon" type="cross" />
                </button>;
          } else {
            clearButton = <EuiButtonEmpty aria-label={clearSelectedFiles} className="euiFilePicker__clearButton" size="xs" onClick={this.removeFiles}>
                  <EuiI18n token="euiFilePicker.removeSelected" default="Remove" />
                </EuiButtonEmpty>;
          }
        } else {
          clearButton = null;
        }
        const loader = !normalFormControl && isLoading && <EuiProgress size="xs" color="accent" position="absolute" />;
        return <div className={classes}>
              <div className="euiFilePicker__wrap">
                <EuiValidatableControl isInvalid={isInvalid}>
                  <input type="file" id={id} name={name} className="euiFilePicker__input" onChange={this.handleChange} ref={input => {
                this.fileInput = input;
              }} onDragOver={this.showDrop} onDragLeave={this.hideDrop} onDrop={this.hideDrop} disabled={disabled} aria-describedby={promptId} {...rest} />
                </EuiValidatableControl>
                <div className="euiFilePicker__prompt" id={promptId}>
                  <EuiIcon className="euiFilePicker__icon" color={isInvalid ? 'danger' : disabled ? 'subdued' : 'primary'} type={isInvalid ? 'alert' : 'importAction'} size={normalFormControl ? 'm' : 'l'} aria-hidden="true" />
                  <div className="euiFilePicker__promptText">
                    {this.state.promptText || initialPromptText}
                  </div>
                  {clearButton}
                  {loader}
                </div>
              </div>
            </div>;
      }}
      </EuiI18n>;
  }
}
EuiFilePicker.propTypes = {
  id: PropTypes.string,
  name: PropTypes.string,
  className: PropTypes.string,
  /**
     * The content that appears in the dropzone if no file is attached
     */
  initialPromptText: PropTypes.node,
  /**
     * Use as a callback to access the HTML FileList API
     */
  onChange: PropTypes.func,
  /**
     * Reduces the size to a typical (compressed) input
     */
  compressed: PropTypes.bool,
  /**
     * Size or type of display;
     * `default` for normal height, similar to other controls;
     * `large` for taller size
     */
  display: PropTypes.oneOf(["default", "large"]),
  /**
     * Expand to fill 100% of the parent.
     * Defaults to `fullWidth` prop of `<EuiForm>`.
     * @default false
     */
  fullWidth: PropTypes.bool,
  isInvalid: PropTypes.bool,
  isLoading: PropTypes.bool,
  disabled: PropTypes.bool,
  "aria-label": PropTypes.string,
  "data-test-subj": PropTypes.string,
  css: PropTypes.any
};
try {
  EuiFilePicker.__docgenInfo = {
    tags: {},
    filePath: '/app/src/components/form/file_picker/file_picker.tsx',
    description: '',
    displayName: 'EuiFilePicker',
    methods: [],
    props: {
      id: {
        defaultValue: null,
        description: '',
        name: 'id',
        parent: {
          fileName: 'app/src/components/form/file_picker/file_picker.tsx',
          name: 'EuiFilePickerProps'
        },
        declarations: [{
          fileName: 'app/src/components/form/file_picker/file_picker.tsx',
          name: 'EuiFilePickerProps'
        }],
        required: false,
        type: {
          name: 'string'
        }
      },
      name: {
        defaultValue: null,
        description: '',
        name: 'name',
        parent: {
          fileName: 'app/src/components/form/file_picker/file_picker.tsx',
          name: 'EuiFilePickerProps'
        },
        declarations: [{
          fileName: 'app/src/components/form/file_picker/file_picker.tsx',
          name: 'EuiFilePickerProps'
        }],
        required: false,
        type: {
          name: 'string'
        }
      },
      className: {
        defaultValue: null,
        description: '',
        name: 'className',
        parent: {
          fileName: 'app/src/components/form/file_picker/file_picker.tsx',
          name: 'EuiFilePickerProps'
        },
        declarations: [{
          fileName: 'app/src/components/form/file_picker/file_picker.tsx',
          name: 'EuiFilePickerProps'
        }],
        required: false,
        type: {
          name: 'string'
        }
      },
      initialPromptText: {
        defaultValue: {
          value: '(\n' + '      <EuiI18n\n' + '        token="euiFilePicker.promptText"\n' + '        default="Select or drag and drop a file"\n' + '      />\n' + '    )'
        },
        description: 'The content that appears in the dropzone if no file is attached',
        name: 'initialPromptText',
        parent: {
          fileName: 'app/src/components/form/file_picker/file_picker.tsx',
          name: 'EuiFilePickerProps'
        },
        declarations: [{
          fileName: 'app/src/components/form/file_picker/file_picker.tsx',
          name: 'EuiFilePickerProps'
        }],
        required: false,
        type: {
          name: 'ReactNode'
        }
      },
      onChange: {
        defaultValue: null,
        description: 'Use as a callback to access the HTML FileList API',
        name: 'onChange',
        parent: {
          fileName: 'app/src/components/form/file_picker/file_picker.tsx',
          name: 'EuiFilePickerProps'
        },
        declarations: [{
          fileName: 'app/src/components/form/file_picker/file_picker.tsx',
          name: 'EuiFilePickerProps'
        }],
        required: false,
        type: {
          name: '(files: FileList) => void'
        }
      },
      compressed: {
        defaultValue: {
          value: 'false'
        },
        description: 'Reduces the size to a typical (compressed) input',
        name: 'compressed',
        parent: {
          fileName: 'app/src/components/form/file_picker/file_picker.tsx',
          name: 'EuiFilePickerProps'
        },
        declarations: [{
          fileName: 'app/src/components/form/file_picker/file_picker.tsx',
          name: 'EuiFilePickerProps'
        }],
        required: false,
        type: {
          name: 'boolean'
        }
      },
      display: {
        defaultValue: {
          value: 'large'
        },
        description: 'Size or type of display;\n' + '`default` for normal height, similar to other controls;\n' + '`large` for taller size',
        name: 'display',
        parent: {
          fileName: 'app/src/components/form/file_picker/file_picker.tsx',
          name: 'EuiFilePickerProps'
        },
        declarations: [{
          fileName: 'app/src/components/form/file_picker/file_picker.tsx',
          name: 'EuiFilePickerProps'
        }],
        required: false,
        type: {
          name: 'enum',
          raw: '"default" | "large"',
          value: [{
            value: '"default"'
          }, {
            value: '"large"'
          }]
        }
      },
      fullWidth: {
        defaultValue: {
          value: 'false'
        },
        description: 'Expand to fill 100% of the parent.\n' + 'Defaults to `fullWidth` prop of `<EuiForm>`.',
        name: 'fullWidth',
        parent: {
          fileName: 'app/src/components/form/file_picker/file_picker.tsx',
          name: 'EuiFilePickerProps'
        },
        declarations: [{
          fileName: 'app/src/components/form/file_picker/file_picker.tsx',
          name: 'EuiFilePickerProps'
        }],
        required: false,
        type: {
          name: 'boolean'
        }
      },
      isInvalid: {
        defaultValue: null,
        description: '',
        name: 'isInvalid',
        parent: {
          fileName: 'app/src/components/form/file_picker/file_picker.tsx',
          name: 'EuiFilePickerProps'
        },
        declarations: [{
          fileName: 'app/src/components/form/file_picker/file_picker.tsx',
          name: 'EuiFilePickerProps'
        }],
        required: false,
        type: {
          name: 'boolean'
        }
      },
      isLoading: {
        defaultValue: null,
        description: '',
        name: 'isLoading',
        parent: {
          fileName: 'app/src/components/form/file_picker/file_picker.tsx',
          name: 'EuiFilePickerProps'
        },
        declarations: [{
          fileName: 'app/src/components/form/file_picker/file_picker.tsx',
          name: 'EuiFilePickerProps'
        }],
        required: false,
        type: {
          name: 'boolean'
        }
      },
      disabled: {
        defaultValue: null,
        description: '',
        name: 'disabled',
        parent: {
          fileName: 'app/src/components/form/file_picker/file_picker.tsx',
          name: 'EuiFilePickerProps'
        },
        declarations: [{
          fileName: 'app/src/components/form/file_picker/file_picker.tsx',
          name: 'EuiFilePickerProps'
        }],
        required: false,
        type: {
          name: 'boolean'
        }
      },
      'aria-label': {
        defaultValue: null,
        description: '',
        name: 'aria-label',
        parent: {
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        },
        declarations: [{
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        }],
        required: false,
        type: {
          name: 'string'
        }
      },
      'data-test-subj': {
        defaultValue: null,
        description: '',
        name: 'data-test-subj',
        parent: {
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        },
        declarations: [{
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        }],
        required: false,
        type: {
          name: 'string'
        }
      },
      css: {
        defaultValue: null,
        description: '',
        name: 'css',
        parent: {
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        },
        declarations: [{
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        }],
        required: false,
        type: {
          name: 'Interpolation<Theme>'
        }
      }
    },
    extendedInterfaces: ['EuiFilePickerProps', 'CommonProps', 'InputHTMLAttributes', 'HTMLAttributes', 'AriaAttributes', 'DOMAttributes']
  };
} catch (e) {}